import React, { useMemo, useState } from 'react';
import { X, Calendar, DollarSign, Globe } from 'lucide-react';
import { Button } from './Button';
import { Resource } from '../types';
import { formatUSD, formatCOP } from '../utils/formatters';
import styles from './ResourceProjectionModal.module.css';

interface ResourceProjectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    resource: Resource | null;
}

export function ResourceProjectionModal({ isOpen, onClose, resource }: ResourceProjectionModalProps) {
    const [currency, setCurrency] = useState<'USD' | 'COP'>('USD');

    if (!isOpen || !resource) return null;

    const projectionData = useMemo(() => {
        const months = Array.from({ length: 12 }, (_, i) => i + 1);

        const rows = months.map(month => {
            // Logic copied/adapted from CalculatorView projection to ensure consistency
            // Start with the full monthly total (which includes one-time costs spread/amortized if that was the logic, 
            // BUT in CalculatorView the logic is: total is Month 1, then subtract one-time for subsequent months).

            let monthlyCostUSD = resource.monthlyCTC.totalUSD;
            let monthlyCostCOP = resource.monthlyCTC.totalCOP;

            // Identify one-time costs from concepts to subtract them for months > 1
            if (month > 1) {
                const concepts = resource.monthlyCTC.concepts;

                const oneTimeUSD = concepts
                    .filter(c => c.name === 'Hiring' || c.name === 'Safety Study')
                    .reduce((sum, c) => sum + c.amountUSD, 0);

                const oneTimeCOP = concepts
                    .filter(c => c.name === 'Hiring' || c.name === 'Safety Study')
                    .reduce((sum, c) => sum + c.amountCOP, 0);

                if (oneTimeUSD > 0) {
                    // Calculate markups to subtract them too
                    const financingRate = concepts.find(c => c.name === 'Financing')?.percentage || 0;
                    const gaRate = concepts.find(c => c.name === 'G&A Colombia')?.percentage || 0;
                    const managementRate = concepts.find(c => c.name === 'Management Fees')?.percentage || 0;
                    const totalMarkup = 1 + financingRate + gaRate + managementRate;

                    monthlyCostUSD -= (oneTimeUSD * totalMarkup);
                    monthlyCostCOP -= (oneTimeCOP * totalMarkup);
                }
            }

            // For the table display breakdown:
            // "Recurring" is the cost of Month 2+
            // "Additional" is Month 1 Total - Month 2 Total
            // This is a bit of a reverse engineering but accurate for display.

            // Actually, let's calculate the 'Recurring' base first.
            // We can use the logic for Month 2 as the "Recurring" value.
            // And Month 1 "Additional" is the difference.

            // Recalculate Month 2 (Recurring Base)
            let recurringUSD = resource.monthlyCTC.totalUSD;
            let recurringCOP = resource.monthlyCTC.totalCOP;

            const concepts = resource.monthlyCTC.concepts;
            const oneTimeBaseUSD = concepts.filter(c => c.name === 'Hiring' || c.name === 'Safety Study').reduce((sum, c) => sum + c.amountUSD, 0);
            const oneTimeBaseCOP = concepts.filter(c => c.name === 'Hiring' || c.name === 'Safety Study').reduce((sum, c) => sum + c.amountCOP, 0);

            if (oneTimeBaseUSD > 0) {
                const financingRate = concepts.find(c => c.name === 'Financing')?.percentage || 0;
                const gaRate = concepts.find(c => c.name === 'G&A Colombia')?.percentage || 0;
                const managementRate = concepts.find(c => c.name === 'Management Fees')?.percentage || 0;
                const totalMarkup = 1 + financingRate + gaRate + managementRate;

                recurringUSD -= (oneTimeBaseUSD * totalMarkup);
                recurringCOP -= (oneTimeBaseCOP * totalMarkup);
            }

            const total = currency === 'USD' ? monthlyCostUSD : monthlyCostCOP;
            const recurring = currency === 'USD' ? recurringUSD : recurringCOP;
            const additional = Math.max(0, total - recurring); // Should be > 0 only for Month 1

            return {
                month,
                recurring,
                additional,
                total
            };
        });

        const grandTotal = rows.reduce((sum, r) => sum + r.total, 0);
        const avgMonthly = grandTotal / 12;

        return { rows, grandTotal, avgMonthly };
    }, [resource, currency]); // Recalculate when currency changes

    const format = currency === 'USD' ? formatUSD : formatCOP;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <div className={styles.headerTitleGroup}>
                        <h3 className={styles.title}>
                            <Calendar size={20} />
                            Proyección Anual: <span className={styles.resourceName}>{resource.name}</span>
                        </h3>
                    </div>
                    <div className={styles.headerControls}>
                        <div className={styles.currencyToggle}>
                            <button
                                className={`${styles.currencyBtn} ${currency === 'COP' ? styles.active : ''}`}
                                onClick={() => setCurrency('COP')}
                            >
                                <DollarSign size={14} /> COP
                            </button>
                            <button
                                className={`${styles.currencyBtn} ${currency === 'USD' ? styles.active : ''}`}
                                onClick={() => setCurrency('USD')}
                            >
                                <Globe size={14} /> USD
                            </button>
                        </div>
                        <button className={styles.closeButton} onClick={onClose}>
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.summaryGrid}>
                        <div className={styles.summaryCard}>
                            <h3>Total Anual {currency}</h3>
                            <p className={styles.summaryValue}>{format(projectionData.grandTotal)}</p>
                            <span className={styles.summaryLabel}>Costo total 12 meses</span>
                        </div>
                        <div className={styles.summaryCard}>
                            <h3>Promedio Mensual</h3>
                            <p className={styles.summaryValue}>{format(projectionData.avgMonthly)}</p>
                            <span className={styles.summaryLabel}>Costo promedio</span>
                        </div>
                        <div className={styles.summaryCard}>
                            <h3>Salario Base</h3>
                            <p className={styles.summaryValue}>{formatCOP(resource.salaryInfo.baseSalary)}</p>
                            <span className={styles.summaryLabel}>Valor fijo mensual</span>
                        </div>
                    </div>

                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Mes</th>
                                    <th>Concepto</th>
                                    <th>Costo Recurrente ({currency})</th>
                                    <th>Adicionales ({currency})</th>
                                    <th>Total ({currency})</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projectionData.rows.map((row) => (
                                    <tr key={row.month} className={row.additional > 0 ? styles.highlightRow : ''}>
                                        <td>Mes {row.month}</td>
                                        <td>{row.month === 1 && row.additional > 0 ? 'Recurrente + One-Time' : 'Recurrente'}</td>
                                        <td>{format(row.recurring)}</td>
                                        <td>{row.additional > 0 ? format(row.additional) : '-'}</td>
                                        <td className={styles.totalCell}>{format(row.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className={styles.grandTotalRow}>
                                    <td colSpan={2}>TOTAL ANUAL</td>
                                    <td>{format(projectionData.rows.reduce((sum, r) => sum + r.recurring, 0))}</td>
                                    <td>{format(projectionData.rows.reduce((sum, r) => sum + r.additional, 0))}</td>
                                    <td className={styles.totalCell}>{format(projectionData.grandTotal)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div className={styles.actions}>
                    <Button onClick={onClose} variant="primary">
                        Cerrar
                    </Button>
                </div>
            </div>
        </div>
    );
}
